import { List, Map } from 'immutable';
import moment from 'moment';
import { createSelector } from 'reselect';

import { createImmutableSelector } from 'flow/selectors/selector-utils';
import { SortingEntry } from 'flow/types/CallRecord';
import { CallRecordKey } from 'flow/types/EntityKeys';
import { State } from 'flow/types/State';
import { TypedMap } from 'flow/types/TypedMap';
import { getCallRecordDisplayTimestamp } from 'flow/utils/call-record-utils';

const MISSED_QUEUE_CALLS_SHOWN = 4;

// The widget's calls, each as its key and times.
const selectUnhandledQueueCallEntries = (
    state: State,
): Map<CallRecordKey, TypedMap<SortingEntry>> | undefined =>
    state.centralSwitchWidgets.getIn(['unhandledQueueCalls', 'records']);

const selectCallRecordsUniversal = (state: State) =>
    state.callRecordsData.get('callRecordsUniversal');

const displayTime = (call: TypedMap<SortingEntry>) =>
    moment(getCallRecordDisplayTimestamp(call, 'pbx')).valueOf();

// The widget's ended calls, newest first. It reads only the widget's own
// calls, so it sorts again only when they change.
const selectEndedUnhandledQueueCalls = createSelector(
    [selectUnhandledQueueCallEntries],
    (calls) =>
        (calls ?? Map<CallRecordKey, TypedMap<SortingEntry>>())
            .toList()
            .filter((call) => !!call.get('disconnected'))
            .sortBy((call) => -displayTime(call)),
);

/**
 * The keys of the newest ended calls the missed queue calls widget shows, by
 * date, newest first: the shape the PBX call history list takes. A row reads
 * its call from the shared call-records store and shows nothing without it,
 * so a call missing there is left out. A store update that leaves the same
 * calls keeps the object, so the list does not render again.
 */
export const selectMissedQueueCallKeysByDate = createImmutableSelector(
    [
        createSelector(
            [selectEndedUnhandledQueueCalls, selectCallRecordsUniversal],
            (calls, callRecordsUniversal) =>
                calls
                    .toSeq()
                    .filter(
                        (call) => !!callRecordsUniversal?.has(call.get('key')),
                    )
                    .take(MISSED_QUEUE_CALLS_SHOWN)
                    .groupBy((call) =>
                        moment(displayTime(call)).format('YYYY-MM-DD'),
                    )
                    .map(
                        (dateCalls): List<CallRecordKey> =>
                            dateCalls.map((call) => call.get('key')).toList(),
                    )
                    .toOrderedMap(),
        ),
    ],
    (callRecordKeysByDate) => callRecordKeysByDate,
);
